import { OfferPageActions, type FlashSameRoomSelection, type OfferPageDispatchAction, type OfferPageMultiRoomMode, type OfferPageRoom, type OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { getDurationPackageOptions, arePricesLoading } from 'selectors/offerPage/offerPageSelectors'
import { getDurationPackageOptionsByRoom } from 'selectors/offerPage/multipleRoomBookingSelectors'
import { isFlashHotelOffer } from 'lib/offer/offerTypes'
import { isFlashSameRoomPackageAvailable } from 'lib/offer/flashPurchasePolicy'
import { isCompatibleFlashPackage, removeInvalidFlashAllocations } from './flashPackageSelection'
import { getFlashSameRoomSelection, isFlashSameRoomSelectionOf } from 'lib/offer/initialSameRoomSelection'

export type FlashSameRoomSelectionStatus = 'empty' | 'resolving' | 'unavailable' | 'valid'

/** The retained treatment choice must not replace the Control arm's public URL intent. */
export function getFlashSameRoomUrlSelection(offer: Pick<App.HotelOffer, 'id' | 'packages'>, rooms: ReadonlyArray<OfferPageRoom>, selection: FlashSameRoomSelection | undefined, isOptionD: boolean) {
  if (!isOptionD || rooms.length <= 1 || selection?.offerId !== offer.id) return undefined
  const selectedPackage = offer.packages.find(pkg => pkg.uniqueKey === selection.packageKey)
  return selectedPackage ? { roomId: selectedPackage.id, rateId: selectedPackage.roomRate.id } : undefined
}

/** Clearing also drops inactive assignments so mode switching cannot restore them. */
export function clearFlashSameRoomSelection(rooms: Array<OfferPageRoom>): OfferPageDispatchAction {
  return {
    type: OfferPageActions.setFlashSameRoomSelection,
    selection: undefined,
    rooms: rooms.map(room => ({ ...room, roomId: undefined, rateId: undefined, optionalBundleIds: undefined })),
  }
}

export function resolveFlashSameRoomSelection({ selection, offerId, packageOptions, roomCount, requiresRates, checkInventory, loading, availableForPurchase }: {
  selection?: FlashSameRoomSelection
  offerId: string
  packageOptions: ReadonlyArray<App.OfferPackageOptionView>
  roomCount: number
  requiresRates: boolean
  checkInventory: boolean
  loading: boolean
  availableForPurchase: boolean
}): { status: FlashSameRoomSelectionStatus, view?: App.OfferPackageOptionView } {
  if (!selection || selection.offerId !== offerId) return { status: 'empty' }
  if (loading) return { status: 'resolving' }
  const view = packageOptions.find(candidate => isFlashSameRoomSelectionOf(selection, offerId, candidate.package))
  if (!view || !availableForPurchase || !isFlashSameRoomPackageAvailable(view, roomCount, requiresRates, checkInventory)) return { status: 'unavailable' }
  return { status: 'valid', view }
}

/** Mode conversion preserves guest slots and resolves real quotes for the destination mode. */
export function getFlashRoomModeTransition(state: OfferPageState, appState: App.State, mode: OfferPageMultiRoomMode) {
  if (!isFlashHotelOffer(state.offer)) return {}
  const offerId = state.offer.id
  const requiresRates = !!state.offer?.property?.useDynamicCancellationPolicies && !!state.checkInDate
  const destinationState = { ...state, multiRoomMode: mode }
  const pending = arePricesLoading(appState, destinationState)
  if (mode === 'individual') {
    const selection = state.flashSameRoomSelection
    if (!selection || selection.offerId !== offerId) return {}
    const viewsByOccupancy = getDurationPackageOptionsByRoom(state, appState)
    const intendedPackage = state.offer.packages.find(pkg => pkg.uniqueKey === selection.packageKey)
    const rooms = state.rooms.map(room => {
      const view = viewsByOccupancy[room.id]?.find(candidate => isFlashSameRoomSelectionOf(selection, offerId, candidate.package) && isCompatibleFlashPackage(candidate, requiresRates))
      // Flash package/room-rate IDs are stable semantic identities. Pending
      // individual quotes resolve these later; no aggregate supplier quote is copied.
      const pkg = view?.package ?? (pending ? intendedPackage : undefined)
      return pkg ? { ...room, roomId: pkg.id, rateId: pkg.roomRate.id } : room
    })
    return { rooms: pending ? rooms : removeInvalidFlashAllocations({ rooms, viewsByOccupancy, requiresRates, checkInventory: !!state.checkInDate }) }
  }
  const first = state.rooms[0]
  const homogeneous = first?.roomId && first.rateId && state.rooms.every(room => room.roomId === first.roomId && room.rateId === first.rateId)
  const view = homogeneous ? getDurationPackageOptions(state, appState).find(candidate => candidate.package.id === first.roomId && candidate.package.roomRate.id === first.rateId) : undefined
  const intendedPackage = homogeneous ? state.offer.packages.find(pkg => pkg.id === first.roomId && pkg.roomRate.id === first.rateId && pkg.duration === state.duration) : undefined
  const selection: FlashSameRoomSelection | undefined = intendedPackage && (pending || (view && isFlashSameRoomPackageAvailable(view, state.rooms.length, requiresRates, !!state.checkInDate)))
    ? getFlashSameRoomSelection(offerId, intendedPackage)
    : undefined
  return { flashSameRoomSelection: selection }
}
